import {
  YES_NO,
  applicantSection,
  businessSection,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * G4. Wniosek o wydanie (zmianę) pozwolenia zintegrowanego albo pozwolenia sektorowego: na wprowadzanie gazów lub pyłów
 * do powietrza, na wytwarzanie odpadów — art. 180–181, 184, 208 i 210 ustawy — Prawo ochrony środowiska (właściwość:
 * art. 378); opłata skarbowa: część III pkt 40 i 46 załącznika do ustawy o opłacie skarbowej (sprawdzone w tekście
 * jednolitym Dz.U. 2025 poz. 1154). Brak wzoru ogólnopolskiego (treść wniosku określa art. 184 ust. 2 i art. 208 ust. 2 —
 * wniosek dołącza się jako plik); układ jak wzór Powiatu Wołomińskiego — pozwolenie na wytwarzanie odpadów
 * (https://samorzad.gov.pl/attachment/461ceea8-42a9-4844-8496-be3e8e8c62d2, pobrany 22.10.2026): podstawa, instalacja,
 * okres, wykaz dokumentów, oświadczenie o statusie przedsiębiorcy (stawka opłaty). Najpierw pytanie o właściwość
 * (starosta / marszałek). Operat ppoż., postanowienie PSP i zaświadczenia o niekaralności przy pozwoleniu na wytwarzanie
 * odpadów — art. 184 ust. 4 pkt 5–7 POŚ (katalog wiąże je ze zbieraniem/przetwarzaniem — do potwierdzenia z wydziałem).
 */
const PERMIT = (value: string): EbiCondition => ({ field: "rodzajPozwolenia", equals: value });
const INTEGRATED = PERMIT("zintegrowane");
const WASTE = PERMIT("odpady");
const ISSUE: EbiCondition = { field: "tryb", equals: "wydanie" };
const CHANGE: EbiCondition = { field: "tryb", equals: "zmiana" };
const BUSINESS: EbiCondition = { field: "prowadzacy", in: ["przedsiebiorcaCeidg", "jednostka"] };
const LEGAL_ENTITY: EbiCondition = { field: "prowadzacy", equals: "jednostka" };
const COMBUSTION_ASKED: EbiCondition = { field: "rodzajPozwolenia", in: ["zintegrowane", "gazyPyly"] };
const COMBUSTION: EbiCondition = { all: [COMBUSTION_ASKED, { field: "zrodloSpalania", equals: "tak" }] };
const SHORT_USE: EbiCondition = { all: [COMBUSTION, { field: "czas500", equals: "tak" }] };
const WASTE_ASKED: EbiCondition = { field: "rodzajPozwolenia", in: ["zintegrowane", "odpady"] };
const COLLECTING: EbiCondition = { all: [WASTE_ASKED, { field: "zbieraniePrzetwarzanie", equals: "tak" }] };
const FIRE_DOCS: EbiCondition = { any: [WASTE, { all: [INTEGRATED, { field: "zbieraniePrzetwarzanie", equals: "tak" }] }] };
const BASELINE: EbiCondition = { all: [INTEGRATED, { field: "raportPoczatkowyWymagany", equals: "tak" }] };
const ACCIDENTS: EbiCondition = { all: [INTEGRATED, { field: "programAwarii", equals: "tak" }] };
const FEE_FULL: EbiCondition = { all: [ISSUE, BUSINESS, { field: "stawkaOplaty", equals: "duzy" }] };
const FEE_REDUCED: EbiCondition = {
  all: [ISSUE, { any: [{ field: "prowadzacy", equals: "osobaFizyczna" }, { all: [BUSINESS, { field: "stawkaOplaty", in: ["msp", "pozostale"] }] }] }],
};

const G4: IEbiFormDefinition = {
  code: "G4",
  version: 1,
  title: "Wniosek o wydanie pozwolenia zintegrowanego lub pozwolenia sektorowego",
  category: EEbiFormCategory.Environment,
  office: EEbiFormOffice.County,
  description: "Pozwolenie zintegrowane, pozwolenie na wprowadzanie gazów lub pyłów do powietrza albo na wytwarzanie odpadów — wydanie lub zmiana.",
  keywords: ["pozwolenie zintegrowane", "emisja", "gazy", "pyły", "powietrze", "odpady", "wytwarzanie odpadów", "instalacja", "ochrona środowiska"],
  organ: "Starosta (art. 378 ust. 1 POŚ); marszałek województwa w sprawach z art. 378 ust. 2a POŚ",
  legalBasis:
    "art. 180, art. 181 ust. 1, art. 184 i art. 208 ustawy z dnia 27 kwietnia 2001 r. — Prawo ochrony środowiska; art. 42 ustawy z dnia 14 grudnia 2012 r. o odpadach",
  request:
    "Na podstawie art. 180, art. 181 ust. 1 i art. 184 ust. 1 ustawy z dnia 27 kwietnia 2001 r. — Prawo ochrony środowiska wnoszę o wydanie (zmianę) pozwolenia wskazanego we wniosku dla instalacji opisanej poniżej.",
  feeLabel: "od 506 zł",
  fee: "Opłata skarbowa za pozwolenie: 2011 zł (działalność gospodarcza) albo 506 zł (mikro, mali i średni przedsiębiorcy, działalność wytwórcza w rolnictwie, pozostali); zmiana pozwolenia — 50% albo 100% stawki. Przy pozwoleniu zintegrowanym także opłata rejestracyjna (do 12 000 zł) na rachunek NFOŚiGW. Pełnomocnictwo — 17 zł.",
  deadlineLabel: "1–6 miesięcy",
  deadline:
    "Pozwolenie zintegrowane dla instalacji nowej lub po istotnej zmianie — do 6 miesięcy od złożenia wniosku (art. 209 ust. 2 POŚ); pozostałe — do miesiąca, w sprawie szczególnie skomplikowanej do 2 miesięcy (art. 35 KPA).",
  variantField: "rodzajPozwolenia",
  variants: {
    zintegrowane: { title: "Wniosek o wydanie lub zmianę pozwolenia zintegrowanego" },
    gazyPyly: { title: "Wniosek o wydanie lub zmianę pozwolenia na wprowadzanie gazów lub pyłów do powietrza" },
    odpady: { title: "Wniosek o wydanie lub zmianę pozwolenia na wytwarzanie odpadów" },
  },
  sections: [
    {
      code: "wlasciwosc",
      title: "Organ właściwy",
      fields: [
        {
          code: "infoWlasciwosc",
          type: "info",
          label:
            "Pozwolenia wydaje starosta (art. 378 ust. 1 POŚ). Marszałek województwa jest właściwy m.in. dla instalacji kwalifikowanych jako przedsięwzięcia mogące zawsze znacząco oddziaływać na środowisko oraz dla pozwoleń dla instalacji komunalnych, o których mowa w art. 38b ust. 1 pkt 1 ustawy o odpadach (art. 378 ust. 2a POŚ), a regionalny dyrektor ochrony środowiska — na terenach zamkniętych ustalonych przez Ministra Obrony Narodowej (art. 378 ust. 2 POŚ).",
        },
        {
          code: "organWlasciwy",
          type: "radio",
          label: "Instalacja, której dotyczy wniosek",
          required: true,
          options: [
            {
              value: "starosta",
              label: "nie jest przedsięwzięciem mogącym zawsze znacząco oddziaływać na środowisko ani instalacją komunalną — właściwy jest starosta",
            },
            {
              value: "marszalek",
              label: "jest przedsięwzięciem mogącym zawsze znacząco oddziaływać na środowisko albo instalacją komunalną — właściwy jest marszałek województwa",
            },
            { value: "nieWiem", label: "nie wiem — proszę o ustalenie organu właściwego" },
          ],
        },
        {
          code: "infoMarszalek",
          type: "info",
          visibleWhen: { field: "organWlasciwy", equals: "marszalek" },
          label:
            "Wniosek złóż w urzędzie marszałkowskim. Jeśli złożysz go tutaj, starosta przekaże go organowi właściwemu (art. 65 § 1 KPA), co wydłuży załatwienie sprawy.",
        },
      ],
    },
    applicantSection(),
    representationSection(),
    {
      code: "rodzaj",
      title: "Rodzaj pozwolenia",
      fields: [
        {
          code: "rodzajPozwolenia",
          type: "radio",
          label: "Wnoszę o pozwolenie",
          required: true,
          options: [
            { value: "zintegrowane", label: "zintegrowane" },
            { value: "gazyPyly", label: "na wprowadzanie gazów lub pyłów do powietrza" },
            { value: "odpady", label: "na wytwarzanie odpadów" },
          ],
        },
        {
          code: "tryb",
          type: "radio",
          label: "Wniosek dotyczy",
          required: true,
          defaultValue: "wydanie",
          options: [
            { value: "wydanie", label: "wydania pozwolenia" },
            { value: "zmiana", label: "zmiany pozwolenia" },
          ],
        },
        { code: "dotychczasowePozwolenie", type: "text", label: "Znak i data dotychczasowego pozwolenia", required: true, visibleWhen: CHANGE, maxLength: 200 },
      ],
    },
    {
      code: "prowadzacyInstalacje",
      title: "Prowadzący instalację",
      fields: [
        {
          code: "prowadzacy",
          type: "radio",
          label: "Instalację prowadzi",
          required: true,
          options: [
            { value: "osobaFizyczna", label: "osoba fizyczna niebędąca przedsiębiorcą — wnioskodawca albo mocodawca" },
            { value: "przedsiebiorcaCeidg", label: "przedsiębiorca będący osobą fizyczną (CEIDG)" },
            { value: "jednostka", label: "osoba prawna albo jednostka organizacyjna nieposiadająca osobowości prawnej" },
          ],
        },
        {
          code: "stawkaOplaty",
          type: "radio",
          label: "Prowadzący instalację (stawka opłaty skarbowej — część III pkt 40 załącznika do ustawy o opłacie skarbowej)",
          required: true,
          visibleWhen: BUSINESS,
          options: [
            { value: "duzy", label: "prowadzi działalność gospodarczą i nie jest mikro, małym ani średnim przedsiębiorcą — 2011 zł" },
            {
              value: "msp",
              label: "jest mikroprzedsiębiorcą, małym lub średnim przedsiębiorcą albo prowadzi działalność wytwórczą w rolnictwie — 506 zł",
            },
            { value: "pozostale", label: "pozwolenie nie jest związane z działalnością gospodarczą — 506 zł" },
          ],
        },
      ],
    },
    businessSection("Dane przedsiębiorcy prowadzącego instalację", BUSINESS),
    {
      code: "instalacja",
      title: "Instalacja",
      fields: [
        { code: "nazwaInstalacji", type: "text", label: "Nazwa i rodzaj instalacji", required: true, maxLength: 300 },
        { code: "adresZakladu", type: "text", label: "Adres zakładu, na którego terenie prowadzona jest eksploatacja instalacji", required: true, maxLength: 300 },
        {
          code: "tytulPrawnyInstalacji",
          type: "text",
          label: "Tytuł prawny do instalacji",
          required: true,
          width: "half",
          maxLength: 200,
          placeholder: "np. własność, dzierżawa",
        },
        { code: "okresPozwolenia", type: "text", label: "Czas, na jaki ma być wydane pozwolenie", required: true, width: "half", maxLength: 100, placeholder: "np. 10 lat" },
        { code: "rodzajDzialalnosci", type: "textarea", label: "Rodzaj prowadzonej działalności", required: true, maxLength: 3000 },
        {
          code: "infoTresc",
          type: "info",
          label:
            "Pełną treść wniosku — informacje wymagane przez art. 184 ust. 2 POŚ, dla pozwolenia zintegrowanego także art. 208 ust. 2, a dla pozwolenia na wytwarzanie odpadów art. 184 ust. 2b — dołącz jako plik w załącznikach.",
        },
        {
          code: "nowaInstalacja",
          type: "radio",
          label: "Czy instalacja jest nowo uruchamiana lub zmieniana w sposób istotny?",
          required: true,
          options: YES_NO,
        },
        {
          code: "zrodloSpalania",
          type: "radio",
          label: "Czy częścią instalacji jest źródło spalania paliw o nominalnej mocy cieplnej nie mniejszej niż 1 MW i mniejszej niż 50 MW?",
          required: true,
          visibleWhen: COMBUSTION_ASKED,
          options: YES_NO,
        },
        {
          code: "infoZrodloSpalania",
          type: "info",
          visibleWhen: COMBUSTION,
          label:
            "Wniosek musi zawierać także informacje z art. 184 ust. 2c POŚ: kod PKD, REGON, nominalną moc cieplną, rodzaj źródła i paliw (z udziałem procentowym), obowiązujące standardy emisyjne, datę oddania do użytkowania oraz przewidywany czas użytkowania i średnie obciążenie.",
        },
        {
          code: "czas500",
          type: "radio",
          label: "Czy czas użytkowania źródła spalania paliw, liczony jako średnia krocząca, nie przekracza limitu z art. 184 ust. 2d POŚ (500 godzin w ciągu roku)?",
          required: true,
          visibleWhen: COMBUSTION,
          options: YES_NO,
        },
        {
          code: "oswiadczenieCzasu",
          type: "declaration",
          label:
            "Oświadczam, że czas użytkowania źródła spalania paliw, liczony jako średnia krocząca, nie przekracza czasu użytkowania określonego w art. 184 ust. 2d ustawy — Prawo ochrony środowiska.",
          required: true,
          visibleWhen: SHORT_USE,
        },
        {
          code: "zbieraniePrzetwarzanie",
          type: "radio",
          label: "Czy pozwolenie ma uwzględniać zbieranie lub przetwarzanie odpadów?",
          required: true,
          visibleWhen: WASTE_ASKED,
          options: YES_NO,
        },
        {
          code: "raportPoczatkowyWymagany",
          type: "radio",
          label:
            "Czy eksploatacja instalacji obejmuje wykorzystywanie, produkcję lub uwalnianie substancji powodującej ryzyko i występuje możliwość zanieczyszczenia gleby, ziemi lub wód gruntowych na terenie zakładu?",
          required: true,
          visibleWhen: INTEGRATED,
          options: YES_NO,
        },
        {
          code: "programAwarii",
          type: "radio",
          label: "Czy dla zakładu opracowano program zapobiegania awariom lub raport o bezpieczeństwie?",
          required: true,
          visibleWhen: INTEGRATED,
          options: YES_NO,
        },
        {
          code: "infoPostacPapierowa",
          type: "info",
          visibleWhen: INTEGRATED,
          label:
            "Wniosek o wydanie pozwolenia zintegrowanego składa się w postaci papierowej w jednym egzemplarzu wraz z zapisem w postaci elektronicznej (art. 208 ust. 5 i ust. 6 pkt 2 POŚ) — urząd może poprosić o dostarczenie egzemplarza papierowego.",
        },
      ],
    },
    {
      code: "oplaty",
      title: "Opłaty",
      fields: [
        { code: "oplataPelna", type: "fee", label: "Opłata skarbowa za wydanie pozwolenia", feeAmount: 2011, visibleWhen: FEE_FULL },
        { code: "oplataObnizona", type: "fee", label: "Opłata skarbowa za wydanie pozwolenia", feeAmount: 506, visibleWhen: FEE_REDUCED },
        {
          code: "infoOplataZmiana",
          type: "info",
          visibleWhen: CHANGE,
          label:
            "Za zmianę pozwolenia opłata skarbowa wynosi 50% stawki, gdy zmiana dotyczy przedłużenia terminu ważności lub rozszerzenia zakresu działalności, a 100% stawki, gdy treścią zmiany jest kolejny rodzaj działalności (część III pkt 46 załącznika do ustawy o opłacie skarbowej). W innych przypadkach urząd wskaże należną kwotę.",
        },
        {
          code: "infoOplataRejestracyjna",
          type: "info",
          visibleWhen: INTEGRATED,
          label:
            "Warunkiem rozpatrzenia wniosku o pozwolenie zintegrowane jest wniesienie opłaty rejestracyjnej (art. 210 POŚ) w wysokości określonej rozporządzeniem Ministra Środowiska z dnia 27 sierpnia 2014 r. (Dz.U. 2014 poz. 1183), nie wyższej niż 12 000 zł; przy istotnej zmianie instalacji — 50% tej opłaty. Opłatę wnosi się na rachunek NFOŚiGW: 76 1130 1062 0000 0109 9520 0010.",
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "wniosekTresc",
      label: "Wniosek o wydanie pozwolenia — treść wymagana przez art. 184 ust. 2 POŚ (dla pozwolenia zintegrowanego także art. 208 ust. 2)",
      hint: "PDF; dla pozwolenia na wytwarzanie odpadów także informacje z art. 184 ust. 2b, a przy źródle spalania 1–50 MW — z art. 184 ust. 2c.",
      multiple: true,
      required: true,
    },
    { code: "streszczenie", label: "Streszczenie wniosku sporządzone w języku niespecjalistycznym (art. 184 ust. 4 pkt 3 POŚ)", required: true },
    {
      code: "zapisElektroniczny",
      label: "Zapis wniosku w postaci elektronicznej — pliki źródłowe (art. 208 ust. 6 pkt 2 POŚ)",
      hint: "Nieobowiązkowo w EBI — np. edytowalne pliki tekstu i obliczeń; urząd może poprosić o nośnik danych.",
      multiple: true,
      visibleWhen: INTEGRATED,
    },
    {
      code: "obrotPrawny",
      label: "Dokument potwierdzający, że wnioskodawca jest uprawniony do występowania w obrocie prawnym (np. odpis z KRS) — art. 184 ust. 4 pkt 1 POŚ",
      visibleWhen: LEGAL_ENTITY,
      required: true,
    },
    {
      code: "informacjaArt143",
      label: "Informacja o spełnianiu wymogów, o których mowa w art. 143 POŚ",
      hint: "Instalacja nowo uruchamiana lub zmieniana w sposób istotny (art. 184 ust. 3) — nie dołączaj osobno, jeśli jest w treści wniosku.",
      visibleWhen: { field: "nowaInstalacja", equals: "tak" },
    },
    {
      code: "raportPoczatkowy",
      label: "Raport początkowy o stanie zanieczyszczenia gleby, ziemi i wód gruntowych (art. 208 ust. 2 pkt 4 POŚ)",
      visibleWhen: BASELINE,
      required: true,
    },
    {
      code: "programAwariiKopia",
      label: "Kopia programu zapobiegania awariom lub raportu o bezpieczeństwie (art. 208 ust. 6 pkt 3 POŚ)",
      visibleWhen: ACCIDENTS,
      required: true,
    },
    {
      code: "oplataRejestracyjna",
      label: "Dowód uiszczenia opłaty rejestracyjnej (art. 208 ust. 6 pkt 1 i art. 210 POŚ)",
      visibleWhen: INTEGRATED,
      required: true,
    },
    {
      code: "operatPpoz",
      label: "Operat przeciwpożarowy (art. 184 ust. 4 pkt 5 POŚ, art. 42 ust. 4b pkt 1 ustawy o odpadach)",
      hint: "Wykonany przez rzeczoznawcę do spraw zabezpieczeń przeciwpożarowych.",
      visibleWhen: FIRE_DOCS,
      required: true,
    },
    {
      code: "postanowieniePsp",
      label: "Postanowienie komendanta Państwowej Straży Pożarnej (art. 42 ust. 4c ustawy o odpadach)",
      visibleWhen: FIRE_DOCS,
      required: true,
    },
    {
      code: "niekaralnosc",
      label: "Zaświadczenia o niekaralności prowadzącego instalację (art. 184 ust. 4 pkt 7 POŚ)",
      hint: "Za przestępstwa przeciwko środowisku oraz za przestępstwa z art. 163, 164 lub 168 w związku z art. 163 § 1 Kodeksu karnego — prowadzącego będącego osobą fizyczną albo wspólnika, prokurenta, członka rady nadzorczej lub członka zarządu prowadzącego będącego osobą prawną lub jednostką organizacyjną.",
      multiple: true,
      visibleWhen: FIRE_DOCS,
      required: true,
    },
    {
      code: "tytulTerenu",
      label: "Dokument potwierdzający tytuł prawny do terenu, na którym odpady mają być zbierane lub przetwarzane (art. 41b ustawy o odpadach)",
      visibleWhen: COLLECTING,
      required: true,
    },
    feeProofAttachment(),
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default G4;
